import { can, withOwnerOnly, type AuthzAction } from "@/context/auth/authz";
import { resourceForRoute } from "@/context/auth/routeResources";

/** Lo que el menú mira de un ítem o subítem para decidir si se ve. */
export interface NodoDelMenu {
  resource?: string | string[];
  permissionLink?: string[] | "owner" | "free" | string;
  ownerOnly?: boolean;
  /**
   * Sólo con la caja offline prendida (D5): la web no lo muestra, ni el escritorio
   * con el flag `OFFLINE_ENABLED` apagado.
   */
  soloEscritorio?: boolean;
}

/**
 * ¿Se ve este ítem (o subítem) del menú? Vocab canónico primero: si declara
 * `resource` (id del entitlement), decide `can()` —el mismo punto de decisión
 * que el editor y el borde, con owner-bypass + plan-gate + clinical + grant
 * adentro—. Si todavía usa `permissionLink` (string de ruta, legacy con
 * pérdida), cae al camino viejo hasta que se migre (#49).
 *
 * `ownerOnly` se suma a todo eso (`withOwnerOnly`): el ítem lo ve sólo el
 * dueño, y sólo si además pasa su gate.
 *
 * `soloEscritorio` corta antes que todo fuera del escritorio. Quien no dice
 * dónde corre, no es el escritorio.
 */
export function esVisibleEnElMenu(
  user: any,
  node: NodoDelMenu,
  enElEscritorio = false
): boolean {
  if (node.soloEscritorio && !enElEscritorio) return false;
  return withOwnerOnly(
    user,
    node.ownerOnly,
    visibleSinCorteDeDueno(user, node)
  );
}

function visibleSinCorteDeDueno(user: any, node: NodoDelMenu): boolean {
  if (node.resource) return can(user, node.resource, "view");
  if (node.permissionLink === "free") return true;
  if (node.permissionLink === "owner") return user?.type === "owner";
  if (user?.type === "owner") return true;
  if (node.permissionLink) {
    return checkIfHasPermission(
      user,
      node.permissionLink as string[] | string,
      "read"
    );
  }
  return true;
}

const ACTION: Record<"read" | "write" | "delete", AuthzAction> = {
  read: "view",
  write: "modify",
  delete: "delete",
};

/**
 * ¿El usuario puede `permission` sobre la(s) ruta(s)? Ahora decide `can()` sobre
 * el resource id del entitlement (resuelto con `resourceForRoute`), no un lookup
 * en `user.paths` (lossy, vía `paths.ts`). Mismo vocab que el editor y el borde.
 * Con array, semántica "cualquiera". Interino hasta que los callers pidan
 * `useCan(RESOURCES.X, action)` directo (#49).
 */
export function checkIfHasPermission(
  user: any,
  pathname: string[] | string,
  permission: "read" | "write" | "delete"
): boolean {
  const action = ACTION[permission];
  const paths = Array.isArray(pathname) ? pathname : [pathname];
  return paths.some((p) => can(user, resourceForRoute(p), action));
}
